import dayjs from 'dayjs'
import _ from 'lodash'
import { createSelector } from 'reselect'
import {
  getCurrentHandover,
  getHandoversList,
  getLogCategories,
  getLogCategoryItems,
  getResidentsList,
  getUsersList
} from './Lists'

const getVisibleHours = (state) => state.handovers.hoursVisible
const getHandoverHistory = (state) => state.handovers.history

export const getLatestHandoverTime = createSelector(
  [getHandoversList],
  (handovers) => {
    if (!handovers || !handovers.length) return null
    const cutoff = dayjs().subtract(48, 'h')
    const createdOn = dayjs(handovers[0].createdOn)
    if (handovers[0].createdOn && createdOn.isAfter(cutoff)) {
      return createdOn
    }
    return null
  }
)

export const formatCurrentHandover = createSelector(
  [
    getCurrentHandover,
    getResidentsList,
    getLogCategories,
    getLogCategoryItems,
    getUsersList,
    getLatestHandoverTime,
    getVisibleHours
  ],
  (
    handover,
    residents,
    categories,
    categoryItems,
    users,
    createdOn,
    hoursVisible
  ) => {
    if (!handover || !residents || !categories || !categoryItems || !users)
      return null
    if (!hoursVisible) return {} // to display loading in current handover screen

    const logs = getLogIcons(handover.logs, categoryItems, categories, users)
    const notesWithCarers = getNoteCarers(handover.notes, users)
    const cutoff = createdOn || dayjs().subtract(hoursVisible, 'h')

    return {
      logsByResident: groupLogsByResident({ logs, residents, cutoff }),
      notes: _.filter(notesWithCarers, (note) => {
        return dayjs(note.createdOn).isAfter(cutoff)
      })
    }
  }
)

export const formatHandoverHistory = createSelector(
  [
    getHandoverHistory,
    getResidentsList,
    getLogCategories,
    getLogCategoryItems,
    getUsersList
  ],
  (handovers, residents, categories, categoryItems, users) => {
    if (!handovers || !residents || !categories || !categoryItems || !users)
      return null

    const formattedHandovers = handovers.map((handover) => {
      const logs = getLogIcons(handover.logs, categoryItems, categories, users)
      const notes = getNoteCarers(handover.notes, users)
      const seenBy = handover.seenBy.map((id) => _.find(users, { id }))
      const logsByResident = logs
        ? groupLogsByResident({ logs, residents })
        : null

      return {
        id: handover.id,
        logsByResident,
        notes,
        seenBy,
        createdOn: handover.createdOn,
        createdBy: _.find(users, { id: handover.createdBy }),
        witnessedBy: _.find(users, { id: handover.witnessedBy })
      }
    })
    return groupHandoversByDate(formattedHandovers)
  }
)

const getLogIcons = (logs, categoryItems, categories, users) => {
  // if logs are just IDs, return null.
  // when we fetch a handover's logs this function will execute fully
  if (typeof logs[0] === 'string') return null
  return logs.map((log) => {
    const item = _.find(categoryItems, { id: log.item })
    const category = _.find(categories, { id: log.category })
    const carer = _.find(users, { id: log?.loggedBy })
    const editedBy = _.find(users, { id: log.editedBy })
    const iconUrl = _.get(item, 'icon') || _.get(category, 'icon')

    return {
      ...log,
      itemIcon: { url: iconUrl },
      categoryColor: category ? category.color : null,
      carerName:
        carer && carer.name ? `${carer.name.first} ${carer.name.last}` : null,
      editedBy: editedBy && `${editedBy.name.first} ${editedBy.name.last}`
    }
  })
}

const getNoteCarers = (notes, users) => {
  // if notes are just IDs, return null.
  // when we fetch a handover's notes this function will execute fully
  if (typeof notes[0] === 'string') return null
  return notes.map((note) => {
    const carer = _.find(users, { id: note.createdBy })
    return {
      ...note,
      carer
    }
  })
}

const groupLogsByResident = ({ logs, residents, cutoff }) => {
  return _(logs)
    .groupBy('resident')
    .map((logs, resident) => ({
      logs: _.filter(logs, (log) => {
        if (!cutoff) return log
        return dayjs(log.timeLogged).isAfter(cutoff)
      }),
      resident: _.find(residents, { id: resident })
    }))
    .sortBy('resident.name.first')
    .value()
}

const groupHandoversByDate = (handovers) => {
  return _(handovers)
    .sortBy('createdOn')
    .reverse()
    .groupBy((handover) => {
      return dayjs(handover.createdOn).startOf('day').format('MMM Do')
    })
    .value()
}
